import { Pause, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { usePauseSource, useResumeSource } from "@/hooks/useSources";
import type { SourceConfig } from "@/lib/api";

interface Props {
  source: SourceConfig;
  triggerClassName?: string;
}

/** Duration presets shown in the pause popover. Each maps to a relative
 *  duration in seconds so the operator doesn't have to think about
 *  timestamps for common cases. "Indefinite" sends no duration — the
 *  backend treats that as "until manual resume". */
const PRESETS: { label: string; seconds: number | null }[] = [
  { label: "15 minutes", seconds: 15 * 60 },
  { label: "1 hour", seconds: 60 * 60 },
  { label: "4 hours", seconds: 4 * 60 * 60 },
  { label: "Until tomorrow", seconds: 24 * 60 * 60 },
  { label: "Indefinite", seconds: null },
];

export function SourcePauseMenu({ source, triggerClassName }: Props) {
  const pause = usePauseSource();
  const resume = useResumeSource();
  const [open, setOpen] = useState(false);

  // Resolve effective-paused once per render. The DB flag can lag the
  // actual state if `paused_until` has already passed; we mirror the
  // backend's effective_state() logic so the UI doesn't show "paused"
  // for a source that has effectively auto-resumed.
  const now = useCurrentTime(30_000); // tick every 30s so the countdown moves
  const isPaused =
    source.is_schedule_paused &&
    (source.paused_until === null ||
      new Date(source.paused_until).getTime() > now);
  const remaining = isPaused && source.paused_until
    ? formatRemaining(new Date(source.paused_until).getTime() - now)
    : null;

  if (isPaused) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="text-[11px] text-amber-300"
          title={
            source.paused_reason
              ? `paused: ${source.paused_reason}`
              : "paused"
          }
        >
          paused{remaining ? ` · ${remaining}` : " · indefinitely"}
        </span>
        <Button
          size="sm"
          variant="outline"
          className={triggerClassName}
          onClick={() => {
            resume.mutate(source.id, {
              onSuccess: () => toast.success(`resumed ${source.id}`),
              onError: (err) => toast.error(`resume failed: ${err.message}`),
            });
          }}
          disabled={resume.isPending}
        >
          <Play />
          Resume
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={triggerClassName}
          disabled={pause.isPending}
        >
          <Pause />
          Pause
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1">
        <ul className="flex flex-col">
          {PRESETS.map(({ label, seconds }) => (
            <li key={label}>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-sm px-2.5 py-1.5 text-left text-xs hover:bg-muted focus:bg-muted focus:outline-none"
                onClick={() => {
                  setOpen(false);
                  pause.mutate(
                    {
                      sourceId: source.id,
                      durationSeconds: seconds ?? undefined,
                    },
                    {
                      onSuccess: () =>
                        toast.success(
                          seconds
                            ? `paused ${source.id} for ${label.toLowerCase()}`
                            : `paused ${source.id} indefinitely`,
                        ),
                      onError: (err) =>
                        toast.error(`pause failed: ${err.message}`),
                    },
                  );
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Re-renders at most once per tickMs so paused countdowns move without
 *  thrashing the render tree. Cheap — a single timer per mount. */
function useCurrentTime(tickMs: number): number {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setT(Date.now()), tickMs);
    return () => window.clearInterval(id);
  }, [tickMs]);
  return t;
}

/** Format a positive ms delta as "2h 15m" / "45m" / "30s". Falls back
 *  to "<1m" for very small positive deltas so the badge still reads. */
function formatRemaining(ms: number): string {
  if (ms <= 0) return "expiring";
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
}
